<template>
    <div class="metric-card">
        <!-- Title -->
        <div class="title-section">
            <div class="title">
                {{ title }}
            </div>
        </div>

        <!-- Content -->
        <div class="content-section">
            <!-- Value -->
            <div class="value-section">
                <div class="value">
                    {{ value }}
                </div>
            </div>

            <!-- Change indicator -->
            <div class="change-section">
                <div class="change-indicator">
                    <!-- Arrow icon (only show if there's a change) -->
                    <div
                        v-if="hasChange"
                        :class="['arrow-icon', arrowColorClass]"
                    >
                        <component :is="arrowIcon" />
                    </div>
                    <!-- Change text -->
                    <div :class="['change-text', changeTextColorClass]">
                        {{ changeText }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ArrowUpIcon from '@/pages/OperationsInsights/components/icons/ArrowUpIcon.vue';
import ArrowDownIcon from '@/pages/OperationsInsights/components/icons/ArrowDownIcon.vue';

interface Props {
  title: string;
  value: string | number;
  changeText: string;
  isPositive?: boolean;
  isGoodChange?: boolean;
  hasChange?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    isPositive: true,
    isGoodChange: true,
    hasChange: true
});

const arrowIcon = computed(() => {
    return props.isPositive ? ArrowUpIcon : ArrowDownIcon;
});

const arrowColorClass = computed(() => {
    // Based on the design: green arrows for good changes, red for bad changes
    return props.isGoodChange ? 'success' : 'error';
});

const changeTextColorClass = computed(() => {
    // If no change, use neutral text color, otherwise use semantic colors
    if (!props.hasChange) {
        return 'neutral';
    }
    // Based on the design: green text for good changes, red for bad changes
    return props.isGoodChange ? 'success' : 'error';
});
</script>

<style lang="scss" scoped>
//@use '../../../styles/main' as *;
@use '../../../styles/commonPageStyles' as *;

.metric-card {
  @include flex-column;
  @include card-border;
  width: 100%;
  max-width: 301px;
  padding: $spacing-md;
  align-items: flex-start;
  gap: $spacing-md;
  background: var(--color-white);
}

.title-section {
  display: flex;
  padding: 0 2px;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
}

.title {
  color: var(--color-text-primary);
  @include font-roboto(16px, 400, 27px);
}

.content-section {
  @include flex-column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
}

.value-section {
  display: flex;
  padding: 0 2px;
  justify-content: flex-end;
  align-items: flex-end;
  gap: $spacing-sm;
}

.value {
  color: var(--color-text-primary);
  @include font-roboto(36px, 500, 27px);
}

.change-section {
  display: flex;
  align-items: center;
  gap: $spacing-lg;
  align-self: stretch;
}

.change-indicator {
  display: flex;
  align-items: center;
  gap: $spacing-xs;
}

.arrow-icon {
  @include flex-center;
  width: $spacing-lg;
  height: $spacing-lg;

  :deep(svg) {
    width: $spacing-lg;
    height: $spacing-lg;
  }

  &.success {
    color: var(--color-status-positive);
  }

  &.error {
    color: var(--color-status-negative);
  }
}

.change-text {
  @include font-roboto(16px, 400, normal);

  &.success {
    color: var(--color-status-positive);
  }

  &.error {
    color: var(--color-status-negative);
  }

  &.neutral {
    color: var(--color-status-neutral);
  }
}
</style>
